import { decodeQueryParams, encodeQueryParams, StringParam, NumberParam } from 'serialize-query-params';
export * from 'serialize-query-params';
import { useLocation, generatePath, useParams, useMatch, matchPath } from 'react-router-dom';
import { useRef, useMemo, useCallback } from 'react';
import queryString from 'query-string';
import { useNavigate } from 'react-router';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license at
 * https://github.com/facebook/fbjs/blob/master/LICENSE
 */
/*eslint-disable no-self-compare */
var hasOwnProperty = Object.prototype.hasOwnProperty;
/**
 * inlined Object.is polyfill to avoid requiring consumers ship their own
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
 */
function is(x, y) {
  // SameValue algorithm
  if (x === y) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    // Added the nonzero y check to make Flow happy, but it is redundant
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    // Step 6.a: NaN == NaN
    return x !== x && y !== y;
  }
}
/**
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.

 * @pbeshai modification of shallowEqual to take into consideration a map providing
 * equals functions
 */
function shallowEqual(objA, objB, equalMap) {
  if (is(objA, objB)) {
    return true;
  }
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }
  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) {
    return false;
  }
  // Test for A's keys different from B.
  for (var i = 0; i < keysA.length; i++) {
    var _equalMap$keysA$i$equ, _equalMap$keysA$i;
    var isEqual = (_equalMap$keysA$i$equ = equalMap == null ? void 0 : (_equalMap$keysA$i = equalMap[keysA[i]]) == null ? void 0 : _equalMap$keysA$i.equals) != null ? _equalMap$keysA$i$equ : is;
    if (!hasOwnProperty.call(objB, keysA[i]) || !isEqual(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }
  return true;
}

var useQueryParamsDefaultResult = [{}, function () {}];
/**
 * Given a query parameter configuration (mapping query param name to { encode, decode }),
 * return an object with the decoded values and a setter for updating them.
 */
var useQueryParams = function useQueryParams(paramConfigMap) {
  var paramConfigMapRef = useRef(paramConfigMap);
  paramConfigMap = shallowEqual(paramConfigMap, paramConfigMapRef.current) ? paramConfigMapRef.current : paramConfigMap;
  paramConfigMapRef.current = paramConfigMap;
  var location = useLocation();
  var navigate = useNavigate();
  var result = useMemo(function () {
    if (paramConfigMap) {
      return decodeQueryParams(paramConfigMap, queryString.parse(location.search));
    }
    return {};
  }, [location.search]);
  var resultRef = useRef(result);
  resultRef.current = result;
  var setValue = useCallback(function (changes, updateType) {
    var changesToUse = typeof changes === 'function' ? changes(resultRef.current) : changes;
    var newQueryParams;
    if (updateType === 'push' || updateType === 'replace') {
      newQueryParams = encodeQueryParams(paramConfigMapRef.current, changesToUse);
    } else {
      newQueryParams = encodeQueryParams(paramConfigMapRef.current, _extends({}, resultRef.current, changesToUse));
    }
    navigate('?' + queryString.stringify(newQueryParams), {
      replace: updateType == null ? void 0 : updateType.startsWith('replace')
    });
  }, [navigate]);
  return useMemo(function () {
    return paramConfigMap === null || paramConfigMap === undefined ? useQueryParamsDefaultResult : [result, setValue];
  }, [result, setValue]);
};
/**
 * Helper to get the latest decoded value with smart caching.
 * Abstracted into its own function to allow re-use in a functional setter (#26)
 */
var useQueryParam = function useQueryParam(name, paramConfig) {
  var _useQueryParams2;
  if (paramConfig === void 0) {
    paramConfig = StringParam;
  }
  var _useQueryParams = useQueryParams((_useQueryParams2 = {}, _useQueryParams2[name] = paramConfig, _useQueryParams2)),
    queryParams = _useQueryParams[0],
    setQueryParams = _useQueryParams[1];
  var setValue = useCallback(function (value, updateType) {
    if (typeof value === 'function') {
      setQueryParams(function (oldValues) {
        oldValues[name] = value(oldValues[name]);
        return oldValues;
      }, updateType);
    } else {
      var _setQueryParams;
      setQueryParams((_setQueryParams = {}, _setQueryParams[name] = value, _setQueryParams), updateType);
    }
  }, []);
  return useMemo(function () {
    return [queryParams[name], setValue];
  }, [queryParams[name], setValue]);
};

/**
 *  Generates various hooks and helpers based on URL pattern and optional parameter types.
 *  @param {string} pattern - the URL pattern (e.g. '/products/:id').
 *  @param {any} urlParamsConfig - object that specifies the types of URL parameters in a pattern (e.g. { id: RequiredNumberParam })
 *  @param {any} searchParamsConfig - object that specifies the types of Search (i.e. ?name=flower&color=yellow) parameters (e.g. { name: StringParam })
 */
function createRoute(pattern, urlParamsConfig, searchParamsConfig) {
  return {
    route: pattern == null ? void 0 : pattern.toString(),
    pattern: pattern,
    link: function link(params,
    // Params<ParamParseKey<'/asd/:id/:type?'>> = { id: string | number; type?: string | number | undefined; }
    search) {
      var realUrlParams = pattern.includes(':') ? params : null;
      var realSearchParams = pattern.includes(':') ? search : params;
      var encodedParams = urlParamsConfig ? encodeQueryParams(urlParamsConfig, realUrlParams) : realUrlParams;
      if (encodedParams) {
        Object.keys(encodedParams).forEach(function (key) {
          if (typeof encodedParams[key] !== 'string') encodedParams[key] = encodedParams[key];
        });
      }
      var result = generatePath(pattern, encodedParams);
      if (realSearchParams) {
        var searchString = queryString.stringify(searchParamsConfig ? encodeQueryParams(searchParamsConfig, realSearchParams) : realSearchParams);
        if (searchString) {
          result = result + '?' + searchString;
        }
      }
      return result.replace('*', '');
    },
    useParams: function useParams$1() {
      var params = useParams();
      var _useQueryParams = useQueryParams(searchParamsConfig),
        queryParams = _useQueryParams[0],
        setQueryParams = _useQueryParams[1];
      if (urlParamsConfig) {
        // fix warning 'Passing through parameter * during decoding since it was not configured.'
        // even if you don't have '*' in your pattern it gets added to params
        if (params['*'] && !urlParamsConfig['*']) {
          urlParamsConfig['*'] = StringParam;
        }
        params = decodeQueryParams(urlParamsConfig, params);
      }
      params.queryParams = queryParams;
      params.setQueryParams = setQueryParams;
      return params;
    },
    useMatch: function useMatch$1() {
      var match = useMatch(pattern);
      if (match) {
        if (urlParamsConfig) {
          addWildcardParamIfNotExist(pattern, urlParamsConfig);
          match.params = decodeQueryParams(urlParamsConfig, match.params);
        }
      }
      return match;
    },
    matchPath: function matchPath$1(path) {
      // we create a copy to NOT modify a `pattern` variable (that is used in e.g. `link` method).
      var localPattern = pattern;
      do {
        var currentPattern = localPattern.includes('?') ? localPattern.replaceAll('?', '') : localPattern;
        var match = matchPath(currentPattern, path);
        if (match) {
          if (urlParamsConfig) {
            addWildcardParamIfNotExist(localPattern, urlParamsConfig);
            match.params = decodeQueryParams(urlParamsConfig, match.params);
          }
        } else if (localPattern.includes('?')) {
          localPattern = localPattern.substring(0, localPattern.lastIndexOf(':') - 1);
          continue;
        } else {
          return null;
        }
        return match;
      } while (true);
    }
  };
}
function addWildcardParamIfNotExist(pattern, urlParamsConfig) {
  // fix warning 'Passing through parameter * during decoding since it was not configured.'
  // even if you don't have '*' in your pattern it gets added to params
  if (pattern.includes('*') && !urlParamsConfig['*']) {
    urlParamsConfig['*'] = StringParam;
  }
}

var RequiredNumberParam = {
  encode: function encode(value) {
    return NumberParam.encode(value);
  },
  decode: function decode(value) {
    var result = NumberParam.decode(value);
    if (result === null || result === undefined || isNaN(result)) {
      throw new Error("Unable to convert '" + value + "' to number");
    }
    return result;
  }
};
var RequiredStringParam = {
  encode: function encode(value) {
    return StringParam.encode(value);
  },
  decode: function decode(value) {
    var result = StringParam.decode(value);
    if (result === null || result === undefined) {
      throw new Error("Param isnt defined");
    }
    return result;
  }
};

export { RequiredNumberParam, RequiredStringParam, createRoute, useQueryParam, useQueryParams };
//# sourceMappingURL=react-router-url-params.esm.js.map
